import { CSSObject } from "@emotion/react";
import {
  constants,
  mediaQueryStyles,
  typographyStyles,
} from "@newsela/angelou";

const { mediaQuery: breakpoint } = mediaQueryStyles;
const {
  breakpoints: { BreakpointNames },
  colors: { ui },
} = constants;

const lightBlue = "rgba(133, 183, 253, 0.4)";
const darkBlue = ui.core[700];
const red100 = ui.red[100];
const red700 = ui.red[600];
const white = ui.white[50];
const green100 = ui.green[100];
const green300 = ui.green[300];
const green500 = ui.green[500];
const neutral300 = ui.neutral[300];
const neutral500 = ui.neutral[500];
const neutral700 = ui.neutral[700];

interface PercentageBarGraphCss {
  roundedValue: number | null;
  overrideWithGreen?:
    | {
        bar: boolean;
        label: boolean;
      }
    | undefined;
  isLargeSize?: boolean;
  isLoading?: boolean | undefined;
}

export const containerCss = (isLargeSize = false): CSSObject => {
  return {
    display: "grid",
    gap: isLargeSize ? "4px" : "1px",
  };
};

export const valueCss = ({
  roundedValue,
  isLargeSize,
}: PercentageBarGraphCss): CSSObject => {
  return {
    lineHeight: "150%",
    fontSize: isLargeSize ? "20px" : "16px",
    color: roundedValue === 0 ? red700 : neutral700,
    fontWeight: isLargeSize ? 700 : 400,
  };
};

export const barContainerCss: CSSObject = {};

const getPercentageBarGraphColors = ({
  roundedValue,
  overrideWithGreen,
}: PercentageBarGraphCss) => {
  if (overrideWithGreen?.bar) {
    return {
      light: green100,
      dark: green300,
    };
  }

  if (roundedValue === 0) {
    return {
      light: red100,
      dark: red700,
    };
  }

  if (roundedValue === null) {
    return {
      light: white,
      dark: white,
    };
  }

  return {
    light: lightBlue,
    dark: darkBlue,
  };
};

export const barCss = ({
  roundedValue = 0,
  overrideWithGreen,
  isLargeSize,
  isLoading,
}: PercentageBarGraphCss): CSSObject => {
  const isNullValue = roundedValue === null;
  const colorSchemeColors = getPercentageBarGraphColors({
    roundedValue,
    overrideWithGreen,
  });

  const getDisplayedPercentageValue = () => {
    if (isNullValue) return 0;
    if (isLargeSize) {
      return roundedValue <= 2 ? "2%" : `${roundedValue}%`;
    }
    return roundedValue <= 6 ? "6%" : `${roundedValue}%`;
  };

  const percentageValue = getDisplayedPercentageValue();

  return {
    padding: isLargeSize && !isLoading ? "4px 12px" : "unset",
    width: "100%",
    overflow: "hidden",
    height: isLargeSize ? "32px" : "8px",
    borderRadius: "16px",
    border: isNullValue && !isLoading ? `1px solid ${neutral300}` : "none",
    background: `linear-gradient(to right, ${colorSchemeColors.dark}, ${colorSchemeColors.dark} ${percentageValue}, ${colorSchemeColors.light} ${percentageValue}, ${colorSchemeColors.light})`,
  };
};

export const labelCss = ({
  roundedValue,
  overrideWithGreen,
  isLargeSize,
}: PercentageBarGraphCss): CSSObject => {
  return {
    fontSize: isLargeSize ? "16px" : "12px",
    color: overrideWithGreen?.label
      ? green500
      : roundedValue === 0
        ? red700
        : neutral500,
  };
};

export const innerContentCss: CSSObject = {
  color: neutral700,
  a: {
    color: neutral700,
  },
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  maxWidth: "100%",
  [breakpoint.between("800px", BreakpointNames.md)]: {
    ...typographyStyles.fontSize.textS,
  },
  [breakpoint.between(BreakpointNames.sm, "800px")]: {
    ...typographyStyles.fontSize.textXS,
  },
  [breakpoint.upTo(BreakpointNames.sm)]: {
    display: "none",
  },
};
